import { Linkedin, ShieldCheck, Trash2, ChevronUp } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

function initials(name) {
  return (name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function timeAgo(iso) {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const d = Math.floor(diff / 86400000);
  if (d > 0) return `${d}d ago`;
  const h = Math.floor(diff / 3600000);
  if (h > 0) return `${h}h ago`;
  const m = Math.floor(diff / 60000);
  return m <= 0 ? "just now" : `${m}m ago`;
}

export default function ContributionCard({ item, onUpvote, onRemoveUpvote, onDelete }) {
  const a = item.author;
  return (
    <article
      data-testid={`contribution-${item.id}`}
      className={`border ${item.is_verified ? "border-[#C5A059]" : "border-[#E5E5E0]"} bg-white p-6`}
    >
      <div className="flex items-start gap-4">
        <button
          data-testid={`contribution-upvote-${item.id}`}
          onClick={() => (item.has_upvoted ? onRemoveUpvote(item) : onUpvote(item))}
          disabled={item.is_own}
          className={`flex flex-col items-center gap-1 px-2 py-2 border transition-colors ${
            item.has_upvoted
              ? "border-[#C5A059] bg-[#C5A059] text-[#0A0A0A]"
              : "border-[#E5E5E0] text-[#0A0A0A] hover:border-[#0A0A0A]"
          } ${item.is_own ? "opacity-50 cursor-not-allowed" : ""}`}
          aria-label="Upvote"
          title={item.is_own ? "You cannot upvote your own contribution" : "Mark as helpful"}
        >
          <ChevronUp className="w-4 h-4" />
          <span data-testid={`upvote-count-${item.id}`} className="font-mono text-xs font-medium">{item.upvote_count}</span>
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap mb-3">
            <Avatar className="w-9 h-9 border border-[#E5E5E0]">
              {a.avatar_url ? <AvatarImage src={a.avatar_url} alt={a.name} /> : null}
              <AvatarFallback className="bg-[#F5F5F0] text-[#0A0A0A] text-xs font-medium">
                {initials(a.name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-1.5">
                <span data-testid={`contribution-author-${item.id}`} className="font-serif text-sm font-semibold text-[#0A0A0A]">{a.name}</span>
                {a.is_verified_owner && (
                  <span data-testid={`verified-owner-${item.id}`} title="Verified property owner" className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#0A0A0A] text-white font-mono text-[9px] uppercase tracking-wider">
                    <ShieldCheck className="w-2.5 h-2.5" /> Owner
                  </span>
                )}
                {a.linkedin_url && (
                  <a href={a.linkedin_url} target="_blank" rel="noreferrer noopener" data-testid={`linkedin-${item.id}`} className="text-[#0A66C2] hover:text-[#004182]" aria-label="LinkedIn profile">
                    <Linkedin className="w-4 h-4" />
                  </a>
                )}
              </div>
              {a.bio && <div className="text-[10px] text-[#525252] italic">{a.bio}</div>}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span className="px-2 py-0.5 bg-[#F5F5F0] text-[#0A0A0A] font-mono text-[10px] uppercase tracking-wider">
                {item.category_label}
              </span>
              {item.is_verified && (
                <span title="Verified insight" className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#C5A059] text-[#0A0A0A] font-mono text-[10px] uppercase tracking-wider">
                  <ShieldCheck className="w-3 h-3" /> Verified
                </span>
              )}
            </div>
          </div>

          <h4 className="font-serif text-xl font-bold text-[#0A0A0A] leading-snug mb-2">{item.title}</h4>
          <p className="text-[#0A0A0A] leading-relaxed whitespace-pre-wrap">{item.body}</p>

          <div className="mt-4 flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-[#525252]">
            <span>{timeAgo(item.created_at)}</span>
            {item.is_own && (
              <button
                data-testid={`contribution-delete-${item.id}`}
                onClick={() => onDelete(item)}
                className="inline-flex items-center gap-1 hover:text-[#0A0A0A] transition-colors"
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
